import React from 'react'
import PropTypes from 'prop-types'
import { useGesture } from 'react-use-gesture'

const defaultWheelZoomOptions = {
  bounds: [0.5, 1.5],
  increment: 0.01
}

/**
 * Keeps a given value within the given bounds
 * @param {*} value
 * @param {*} bound
 */
const keepBound = (value, bound) =>
  Math.min(bound[1], Math.max(bound[0], value))

/**
 * Get the closest value in the steps array to the
 * provided value
 */
const getClosestStep = (value, steps) => {
  let distance = 100000
  let closestStepIndex
  for (let i = 0; i < steps.length; i++) {
    const stepDistance = Math.abs(value - steps[i])
    if (stepDistance < distance) {
      closestStepIndex = i
      distance = stepDistance
    }
  }
  return steps[closestStepIndex]
}

/**
 * Creates wheel handler that provides zoom value
 * @param {*} zoom
 * @param {*} onZoom
 * @param {*} options
 */
const makeWheelZoom = (zoom, onZoom, options = {}) => ({
  last,
  movement,
  memo = zoom
}) => {
  const { bounds, increment, steps } = {
    ...defaultWheelZoomOptions,
    ...options
  }
  // last event is returning a smaller value, so ignore
  if (last) return memo
  const zoomLevel = keepBound(
    memo + movement[1] * increment,
    bounds
  )
  const newZoom = getClosestStep(zoomLevel, steps)
  if (memo !== newZoom) onZoom(newZoom)
  return zoomLevel
}

/**
 * Creates a handler function for changing a rotation
 * value based on drag events
 * @param {*} rotation
 * @param {*} onRotate
 */
export const makeDragRotate = (rotation, onRotate) => ({
  movement,
  memo = rotation
}) => {
  // get x / y rotation in degrees
  const x =
    memo[0] + (movement[1] / window.innerWidth) * 180
  const y =
    memo[1] + (movement[0] / window.innerHeight) * 180

  // set the new rotation
  onRotate([x, y, memo[2]])

  return memo
}

const DO_NOTHING = () => {}

const CaptureDragRotate = ({
  enableDrag,
  enableWheel,
  initialRotation,
  onRotate,
  initialZoom,
  onZoom,
  zoomConfig = {},
  children,
  ...props
}) => {
  // get a function that updates rotation on drag
  const dragHandler = enableDrag
    ? makeDragRotate(initialRotation, onRotate)
    : DO_NOTHING

  // boolean to check if wheel zoom is on
  const hasWheelZoom = onZoom && initialZoom && enableWheel

  // wheel event handler
  const wheelHandler = hasWheelZoom
    ? makeWheelZoom(initialZoom, onZoom, zoomConfig)
    : DO_NOTHING

  const bind = useGesture({
    onDrag: dragHandler,
    onWheel: wheelHandler
  })

  return (
    <div {...bind()} {...props}>
      {children}
    </div>
  )
}

CaptureDragRotate.propTypes = {
  initialRotation: PropTypes.array,
  onRotate: PropTypes.func
}

export default CaptureDragRotate
